import React, { useState } from 'react';
import { ENERGY, MOOD, RPE_HINTS, faceFor, routineLabel } from './diaryScales';
import './Diary.css';

const dayLabel = (date) => new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const needsAfter = (e) => e.rpe == null || e.durationMinutes == null;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// "3 skills, 1 routine"
function tracked(rows) {
  const routines = rows.filter(r => r.isRoutine).length;
  const skills = rows.length - routines;
  return [skills && plural(skills, 'skill'), routines && plural(routines, 'routine')].filter(Boolean).join(', ');
}

const face = f => (f ? `${f.emoji} ${f.label}` : null);

// Read-only list of entries, newest first; each row expands to the details.
// Edit and Delete only show when canWrite. Coaches can open every entry at once.
// Entries written with no signal show as waiting to send, or as not saved (with
// Discard) if the server refused them.
export default function DiaryEntryList({ entries, canWrite, onEdit, onDelete, onDiscard, allowShowAll = false }) {
  const [open, setOpen] = useState(() => new Set());
  if (entries.length === 0) return <p className="diary-empty">No sessions logged yet.</p>;

  const allOpen = entries.every(e => open.has(e.id));
  const toggle = id => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <>
    {allowShowAll && (
      <div className="diary-list__show-all">
        <button type="button" className="bk-btn bk-btn--outline bk-btn--sm" onClick={() => setOpen(allOpen ? new Set() : new Set(entries.map(e => e.id)))}>
          {allOpen ? 'Hide details' : 'Show all details'}
        </button>
      </div>
    )}
    <ul className="diary-list">
      {entries.map(e => {
        const energy = faceFor(ENERGY, e.energy);
        const mood = faceFor(MOOD, e.mood);
        const expanded = open.has(e.id);
        return (
          <li key={e.id} className="diary-list__item">
            <button type="button" className="diary-list__row" aria-expanded={expanded} onClick={() => toggle(e.id)}>
              <span className="diary-list__date">{dayLabel(e.date)}</span>
              <span title={energy ? `Energy: ${energy.label}` : 'Energy not recorded'}>{energy ? energy.emoji : '·'}</span>
              <span title={mood ? `Mood: ${mood.label}` : 'Mood not recorded'}>{mood ? mood.emoji : '·'}</span>
              <span>{e.rpe != null ? `RPE ${e.rpe}` : ''}</span>
              <span>{e.durationMinutes != null ? `${e.durationMinutes} min` : ''}</span>
              <span>{tracked(e.skills)}</span>
              {needsAfter(e) && <span className="diary-badge">Finish after session</span>}
              {e.pending && <span className="diary-badge diary-badge--waiting">Waiting to send</span>}
              {e.failed && <span className="diary-badge diary-badge--failed">Not saved</span>}
            </button>
            {expanded && (
              <div className="diary-list__details">
                <dl className="diary-detail">
                  {[
                    ['Energy', face(energy)],
                    ['Mood', face(mood)],
                    ['How hard it felt', e.rpe != null ? `RPE ${e.rpe}${RPE_HINTS[e.rpe] ? ` (${RPE_HINTS[e.rpe]})` : ''}` : null],
                    ['Duration', e.durationMinutes != null ? `${e.durationMinutes} minutes` : null],
                    ['Goal', e.sessionGoal],
                    ['Last meal', e.lastMealDescription && `${e.lastMealDescription}${e.lastMealAt ? ` at ${timeLabel(e.lastMealAt)}` : ''}`],
                    ['Affecting training', e.factors],
                  ].map(([label, value]) => (
                    <div key={label} className="diary-detail__row">
                      <dt>{label}</dt>
                      <dd className={value ? '' : 'diary-detail__none'}>{value || 'Not recorded'}</dd>
                    </div>
                  ))}
                </dl>
                {e.skills.length > 0 && (
                  // A list rather than a .table: App.css hides tables on phones
                  <ul className="diary-skills">
                    {e.skills.map(s => (
                      <li key={s.id} className="diary-skills__item">
                        <span className="diary-skills__name">
                          {s.isRoutine
                            ? <>Routine: {routineLabel(s)}</>
                            : <>{s.name}{s.figNotation ? ` (${s.figNotation})` : ''}{s.archived ? ' (archived)' : ''}</>}
                        </span>
                        <span className="diary-skills__count">× {s.count}</span>
                        {s.qualityNote && <span className="diary-skills__note">{s.qualityNote}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {e.failed && <p className="diary-list__failed">Not saved: {e.failed}. Fix it with Edit, or discard it.</p>}
                {canWrite && (
                  <div className="diary-list__actions">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => onEdit(e)}>Edit</button>
                    {e.failed && onDiscard
                      ? <button type="button" className="btn btn-danger btn-sm" onClick={() => onDiscard(e)}>Discard</button>
                      : <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(e)}>Delete</button>}
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
    </>
  );
}
